// src/utils/accessControl.js
//
// Time-limited platform access — pure helpers and constants.
// The single source of truth for whether a user may use the platform.
//
// A user is the flat Strapi record and may carry:
//   accessExpiresAt : ISO datetime string | null   -> null means UNLIMITED access
//   plan            : 'unlimited' | 'trial' | 'paid'
//   planStartedAt   : ISO datetime string | null
//   trialWeeks      : number | null
//
// Access rule: a user has access when there is no expiry, or now is before it.
// Missing / malformed data fails OPEN (treated as unlimited) on purpose: this gate
// is UX only, and backend hard-enforcement is a later phase. We never want a data
// hiccup to lock a paying customer out.

export const PLAN = {
  UNLIMITED: 'unlimited',
  TRIAL: 'trial',
  PAID: 'paid',
};

// Allowed paid subscription durations (months) and the default selection.
export const PAID_DURATION_MONTHS = [6, 12, 24];
export const DEFAULT_PAID_DURATION_MONTHS = 12;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Returns the plan string for a user, defaulting to 'unlimited'.
export function getPlan(user) {
  return user?.plan || PLAN.UNLIMITED;
}

// true when the user has no expiry set (null/undefined/empty) => unlimited access.
export function isUnlimited(user) {
  const raw = user?.accessExpiresAt;
  return raw === null || raw === undefined || raw === '';
}

// Parses accessExpiresAt to a timestamp (ms). Returns null when unlimited or invalid.
function expiryTime(user) {
  if (isUnlimited(user)) return null;
  const t = new Date(user.accessExpiresAt).getTime();
  return Number.isNaN(t) ? null : t;
}

// Core check. true when the user is allowed to use the platform.
// Unlimited or unparseable expiry => true (fail open).
export function hasAccess(user) {
  const t = expiryTime(user);
  if (t === null) return true;
  return Date.now() < t;
}

// true only when an expiry is set AND it is in the past.
export function isAccessExpired(user) {
  const t = expiryTime(user);
  if (t === null) return false;
  return Date.now() >= t;
}

// Whole days remaining until expiry (ceil). null when unlimited/unknown.
// Can be 0 or negative when already expired.
export function daysRemaining(user) {
  const t = expiryTime(user);
  if (t === null) return null;
  return Math.ceil((t - Date.now()) / MS_PER_DAY);
}

// --- date math (pure) -------------------------------------------------------

// Returns a new Date = date + months. Uses setMonth (calendar rollover, e.g.
// Jan 31 + 1 month -> early Mar). Accepts a Date or anything Date can parse.
export function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

// Returns a new Date = date + weeks (7 days each).
export function addWeeks(date, weeks) {
  return new Date(new Date(date).getTime() + weeks * 7 * MS_PER_DAY);
}

// ISO expiry for a trial of `weeks` weeks starting at `from` (default now).
export function computeTrialExpiry(weeks, from = new Date()) {
  return addWeeks(from, weeks).toISOString();
}

// ISO expiry for a paid plan of `months` months starting at `from` (default now).
export function computePaidExpiry(months, from = new Date()) {
  return addMonths(from, months).toISOString();
}
